import { useMemo, useState } from 'react'
import { IntegrationTargets } from '../components/IntegrationTargets.js'
import { useApi, useAsync } from '../lib/useApi.js'
import { CopyButton, Dialog, ErrorBox, Loading, relative, until } from '../components/ui.js'
import {
  BUILD_PROMPT,
  CLIENTS,
  TOKEN_PLACEHOLDER,
  VERIFY_PROMPT,
  mcpUrl,
  type Client,
  type Snippet,
} from '../lib/clients.js'

/**
 * Connect your coding agent.
 *
 * The page that turns "NimblyBase has an MCP server" into "my agent can build
 * my backend". Two things it is careful about:
 *
 * - The config block shows a PLACEHOLDER until you deliberately mint a token.
 *   A page that renders a live credential on load is a page that leaks one
 *   into every screenshot and screen-share of it.
 * - Every token it issues is named after the client it was made for, and the
 *   list of them is right here with one-click revoke. That is the security
 *   property that actually matters for a pasted credential: not that it is
 *   short-lived, but that you can see which agent has it and take it away.
 */
export default function ConnectAgent() {
  const api = useApi()
  const base = import.meta.env.VITE_API_URL ?? 'https://api-uat.nimblybase.com'
  const url = useMemo(() => mcpUrl(base), [base])

  const [selected, setSelected] = useState<Client>(CLIENTS[0]!)
  const [issued, setIssued] = useState<{ name: string; token: string } | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const projects = useAsync(() => api.listProjects(), [api])
  const connections = useAsync(() => api.listOAuthConnections(), [api, reloadKey])
  const tokens = useAsync(() => api.listAccessTokens(), [api, reloadKey])

  // A minted token stays visible across tabs, labelled with the name it was
  // created under. Binding it to the tab it was made in was the first
  // version, and it meant switching tabs to compare two clients destroyed a
  // credential that can never be shown again - a worse trap than a token
  // named "Cursor agent" appearing in the VS Code block, which works fine.
  /**
   * Sign-in is the default, and the token path is the fallback — V1-18.
   *
   * Until this existed the page taught one thing: mint a credential and
   * paste it into a file. That is now the worse of the two options and
   * was the only one on offer, which is how a finished authorization
   * server stays invisible to the people it was built for.
   */
  const [mode, setMode] = useState<'oauth' | 'token'>('oauth')
  const oauth = mode === 'oauth'

  const live = issued !== null
  const ctx = { url, token: issued?.token ?? TOKEN_PLACEHOLDER, live, oauth }

  const preamble = selected.preamble?.(ctx) ?? null
  // Numbered from what is actually shown. Codex loses its preamble under
  // OAuth, and a page that jumps from step 1 to step 3 looks broken.
  const step = (n: number) => (preamble ? n : n - 1)

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Connect your coding agent</h1>
          <p className="sub">
            Your agent works in the workspace you approve. OAuth access is limited by the approved scopes
            and your current role. Connections cover the workspace unless you explicitly restrict one below. Linking a repository does not restrict its authorization.
          </p>
        </div>
      </div>

      <IntegrationTargets apiOrigin={base} />

      <details style={{ marginTop: 24 }}>
        <summary>Advanced MCP and CLI configuration</summary>
      <div className="tabs" role="group" aria-label="Coding agent">
        {CLIENTS.map((c) => (
          <button
            key={c.id}
            aria-pressed={c.id === selected.id}
            className={c.id === selected.id ? 'tab tab-on' : 'tab'}
            onClick={() => setSelected(c)}
          >
            {c.name}
          </button>
        ))}
      </div>

      <p className="sub" style={{ margin: '12px 0 16px' }}>
        {selected.blurb}
      </p>

      <div className="tabs" role="group" aria-label="How your agent signs in">
        <button
          aria-pressed={oauth}
          className={oauth ? 'tab tab-on' : 'tab'}
          onClick={() => setMode('oauth')}
        >
          Sign in from the agent
        </button>
        <button
          aria-pressed={!oauth}
          className={!oauth ? 'tab tab-on' : 'tab'}
          onClick={() => setMode('token')}
        >
          Paste a token
        </button>
      </div>

      {oauth ? (
        <>
          <p className="sub small" style={{ margin: '12px 0 16px' }}>
            No credential goes in the config. Your agent reads this URL, asks you to approve it here, and
            gets its own access — limited to what you approve, and revocable without touching anything else.
            If your client does not support this yet, use a token instead.
          </p>
          {/* Said on the client it applies to, so the promise above is
              never quietly untrue for one entry. */}
          {selected.signinCaveat && (
            <p className="notice small" style={{ margin: '0 0 16px' }}>
              {selected.signinCaveat}
            </p>
          )}
        </>
      ) : (
        <TokenButton
          client={selected}
          issued={issued}
          onIssued={(name, token) => {
            setIssued({ name, token })
            setReloadKey((n) => n + 1)
          }}
        />
      )}

      {preamble && (
        <Step n={1} title={selected.id === 'cli' ? 'Install it' : 'Put the token in your environment'}>
          <Code snippet={preamble} live={ctx.live} />
        </Step>
      )}

      <Step
        n={step(2)}
        title={selected.setupTitle ?? (selected.configPath ? 'Add this to your config' : 'Run this')}
        note={selected.configPath}
      >
        <Code snippet={selected.snippet(ctx)} live={ctx.live} />
      </Step>

      {oauth && (
        <Step n={step(3)} title="Approve it">
          <p className="sub small" style={{ marginTop: 0 }}>
            Your agent will open a page here, or show you a short code to enter. You will see the
            application’s name and exactly what it is asking for before anything is granted.
          </p>
        </Step>
      )}

      <Step n={oauth ? step(4) : step(3)} title="Check it worked">
        <p className="sub small" style={{ marginTop: 0 }}>
          Ask your agent:
        </p>
        <Code snippet={{ language: 'bash', text: VERIFY_PROMPT }} prose />
        <p className="sub small">
          It should answer with your projects. If it says it has no such tool, the config was not picked up -
          most clients need a restart.
        </p>
      </Step>

      <Step n={oauth ? step(5) : step(4)} title="Then build something">
        <Code snippet={{ language: 'bash', text: BUILD_PROMPT }} prose />
      </Step>

      <p className="sub small">
        The server is <code>{url}</code> ·{' '}
        <a href={selected.docs} target="_blank" rel="noreferrer">
          {selected.name} MCP docs
        </a>
      </p>

      </details>

      <h2>Authorized app connections</h2>
      <p className="sub small">
        These are your approvals in this workspace. App names are supplied by the app and are not a verified publisher identity.
        Each row is one authorization; disconnecting it keeps your other connections and dashboard login intact.
      </p>
      <div className="card">
        {connections.loading ? <Loading /> : connections.error ? (
          <ErrorBox retry={connections.reload}>{connections.error}</ErrorBox>
        ) : !connections.data?.enabled ? (
          <div className="empty">App authorization is unavailable in this environment.</div>
        ) : !connections.data.connections.length ? (
          <div className="empty">No app connections in this workspace yet.</div>
        ) : connections.data.connections.map((c) => (
          <div className="row" key={c.id}>
            <div className="row-main">
              <div className="row-title">{c.clientName} · {c.status === 'ready' ? 'Authorized' : 'Awaiting token exchange'}</div>
              <div className="row-sub">
                <code>Client {c.clientId}</code> · authorized {relative(c.connectedAt)}
                {' · '}{c.lastUsedAt ? `last used ${relative(c.lastUsedAt)}` : 'never used'}
                {' · '}renewal expires {until(c.expiresAt)}
              </div>
              <div className="row-sub">Approved scopes: {c.scopes.join(', ') || 'none'}</div>
              <div className="row-sub">Access: {c.projectId ? `project ${c.projectName ?? c.projectId} only` : 'this workspace'}</div>
              {!c.projectId && <Restrict id={c.id} name={c.clientName} clientId={c.clientId} projects={projects.data?.projects ?? []}
                unavailable={projects.loading || Boolean(projects.error)} onDone={() => setReloadKey((n) => n + 1)} />}
            </div>
            <Disconnect id={c.id} name={c.clientName} clientId={c.clientId}
              access={c.projectId ? `project ${c.projectName ?? c.projectId} only` : 'this workspace'}
              onDone={() => setReloadKey((n) => n + 1)} />
          </div>
        ))}
      </div>
      <p className="sub small">Disconnect blocks new management requests and refreshes. An operation already accepted may finish.</p>

      <h2>Tokens you have issued</h2>
      <div className="card">
        {tokens.loading ? (
          <Loading />
        ) : tokens.error ? (
          <ErrorBox retry={tokens.reload}>{tokens.error}</ErrorBox>
        ) : !tokens.data?.tokens.length ? (
          <div className="empty">None yet. Create one above.</div>
        ) : (
          tokens.data.tokens.map((t) => (
            <div className="row" key={t.id}>
              <div className="row-main">
                <div className="row-title">{t.name}</div>
                <div className="row-sub">
                  <code>{t.tokenPrefix}…</code> · created {relative(t.createdAt)}
                  {' · '}
                  {t.lastUsedAt ? `last used ${relative(t.lastUsedAt)}` : 'never used'}
                  {t.expiresAt ? ` · expires ${until(t.expiresAt)}` : ''}
                </div>
              </div>
              <Revoke id={t.id} name={t.name} onDone={() => setReloadKey((n) => n + 1)} />
            </div>
          ))
        )}
      </div>
      <p className="sub small">
        Revoking one blocks new requests after propagation. Your other agents keep working - which is why each
        gets its own.
      </p>
    </>
  )
}

function Step({
  n,
  title,
  note,
  children,
}: {
  n: number
  title: string
  note?: string
  children: React.ReactNode
}) {
  return (
    <section className="step">
      <h2 className="step-title">
        <span className="step-n">{n}</span> {title}
      </h2>
      {note && (
        <p className="sub small mono" style={{ margin: '0 0 8px' }}>
          {note}
        </p>
      )}
      {children}
    </section>
  )
}

/**
 * `prose` is for the prompts. They are sentences, not code - line breaks mean
 * nothing in them - so they wrap rather than scrolling sideways under the
 * copy button, which is what the longest one did.
 */
function Code({ snippet, live, prose }: { snippet: Snippet; live?: boolean; prose?: boolean }) {
  return (
    <div className={['code', live ? 'code-live' : '', prose ? 'code-prose' : ''].filter(Boolean).join(' ')}>
      <pre>
        <code>{snippet.text}</code>
      </pre>
      <CopyButton value={snippet.text} />
    </div>
  )
}

function TokenButton({
  client,
  issued,
  onIssued,
}: {
  client: Client
  issued: { name: string; token: string } | null
  onIssued: (name: string, token: string) => void
}) {
  const api = useApi()
  const [days, setDays] = useState('30')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (issued) {
    return (
      <div className="notice">
        <strong>“{issued.name}” created and pasted into the block below — it will not be shown again.</strong>
        <p className="sub small" style={{ margin: '6px 0 0' }}>
          It works for any client on this page. Copy the config now; if you lose it, revoke it below and make
          another.
        </p>
      </div>
    )
  }

  return (
    <form
      className="card"
      style={{ padding: 14, marginBottom: 16 }}
      onSubmit={async (e) => {
        e.preventDefault()
        setBusy(true)
        setError(null)
        try {
          // Named after the client, so the list below says which agent has
          // which token - and revoking the right one is obvious.
          const name = `${client.name} agent`
          const result = await api.createAccessToken({ name, expiresInDays: Number(days) })
          onIssued(name, result.token.plaintext)
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Could not create a token.')
        } finally {
          setBusy(false)
        }
      }}
    >
      {error && <ErrorBox>{error}</ErrorBox>}
      <div className="form-row">
        <div style={{ flex: 1 }}>
          <strong>Create a token for {client.name}</strong>
          <p className="sub small" style={{ margin: '2px 0 0' }}>
            The block below shows a placeholder until you do. Nothing secret is on this page before you ask
            for it.
          </p>
        </div>
        <div className="field" style={{ width: 150, marginBottom: 0 }}>
          <label htmlFor="tok-days">Expires</label>
          <select id="tok-days" value={days} onChange={(e) => setDays(e.target.value)}>
            <option value="1">in 1 day</option>
            <option value="7">in 7 days</option>
            <option value="30">in 30 days</option>
            <option value="90">in 90 days</option>
          </select>
        </div>
        <button className="btn btn-primary" disabled={busy}>
          {busy ? 'Creating…' : 'Create token'}
        </button>
      </div>
    </form>
  )
}

function Revoke({ id, name, onDone }: { id: string; name: string; onDone: () => void }) {
  const api = useApi()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  return <>
    <button type="button" className="btn btn-danger" disabled={busy} onClick={() => { setError(null); setOpen(true) }}>Revoke</button>
    <Confirmation open={open} title={`Revoke "${name}"?`} busy={busy} error={error}
      confirmLabel="Revoke token" onClose={() => setOpen(false)} onConfirm={async () => {
        if (busy) return
        setBusy(true); setError(null)
        try { await api.revokeAccessToken(id); setOpen(false); onDone() }
        catch (err) { setError(err instanceof Error ? err.message : 'Could not revoke this token.') }
        finally { setBusy(false) }
      }}>
      <p>New requests from this token will be refused after propagation. Other tokens and app connections remain active.</p>
    </Confirmation>
  </>
}

function Disconnect({ id, name, clientId, access, onDone }: {
  id: string; name: string; clientId: string; access: string; onDone: () => void
}) {
  const api = useApi()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  return <>
    <button type="button" className="btn btn-danger" disabled={busy} onClick={() => { setError(null); setOpen(true) }}>Disconnect</button>
    <Confirmation open={open} title={`Disconnect "${name}"?`} busy={busy} error={error}
      confirmLabel="Disconnect authorization" onClose={() => setOpen(false)} onConfirm={async () => {
        if (busy) return
        setBusy(true); setError(null)
        try { await api.revokeOAuthConnection(id); setOpen(false); onDone() }
        catch (err) { setError(err instanceof Error ? err.message : 'Could not disconnect this app.') }
        finally { setBusy(false) }
      }}>
      <p>Client <code>{clientId}</code></p>
      <p>Access: {access}</p>
      <p>This authorization will no longer work. Other connections and your dashboard login remain active. An operation already accepted may finish.</p>
    </Confirmation>
  </>
}

function Restrict({ id, name, clientId, projects, unavailable, onDone }: {
  id: string; name: string; clientId: string; projects: { id: string; name: string }[]; unavailable: boolean; onDone: () => void
}) {
  const api = useApi()
  const [projectId, setProjectId] = useState('')
  const [target, setTarget] = useState<{ id: string; name: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  return <div>
    <label className="small" htmlFor={`restrict-${id}`}>Restrict this authorization to a project</label>
    <div className="row">
      <select id={`restrict-${id}`} value={projectId} disabled={busy || unavailable} onChange={e => setProjectId(e.target.value)}>
        <option value="">Choose a project…</option>
        {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <button type="button" className="btn" disabled={busy || unavailable || !projects.some(p => p.id === projectId)} onClick={() => {
        const chosen = projects.find(p => p.id === projectId)
        if (chosen) { setError(null); setTarget({ id: chosen.id, name: chosen.name }) }
      }}>{busy ? 'Restricting…' : 'Restrict to project'}</button>
    </div>
    <Confirmation open={target !== null} title={`Restrict "${name}"?`} busy={busy} error={error}
      confirmLabel="Restrict authorization" onClose={() => setTarget(null)} onConfirm={async () => {
        if (busy || !target) return
        if (unavailable || !projects.some(p => p.id === target.id)) { setError('That project is no longer available. Cancel and choose a project again.'); return }
        setBusy(true); setError(null)
        try { await api.narrowOAuthConnection(id, target.id); setTarget(null); onDone() }
        catch (err) { setError(err instanceof Error ? err.message : 'Could not restrict this authorization.') }
        finally { setBusy(false) }
      }}>
      <p>Client <code>{clientId}</code></p>
      <p>Restrict access to <strong>{target?.name}</strong> only.</p>
      <p>Other projects and creating projects will stop working through this authorization, including any terminals using it. This cannot be widened or moved; authorize again for broader access.</p>
    </Confirmation>
  </div>
}

function Confirmation({ open, title, busy, error, confirmLabel, onClose, onConfirm, children }: {
  open: boolean; title: string; busy: boolean; error: string | null; confirmLabel: string
  onClose: () => void; onConfirm: () => Promise<void>; children: React.ReactNode
}) {
  return <Dialog open={open} title={title} busy={busy} onClose={onClose}>
    {children}
    {error && <ErrorBox>{error}</ErrorBox>}
    <button type="button" className="btn" disabled={busy} onClick={onClose}>Cancel</button>{' '}
    <button type="button" className="btn btn-danger" disabled={busy} onClick={() => { void onConfirm() }}>
      {busy ? 'Working…' : confirmLabel}
    </button>
  </Dialog>
}
